import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './Table.module.css';
import * as FaIcons from 'react-icons/fa';

const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No data available',
  onSort,
  onRowClick,
  sortable = true,
  hoverable = true,
  striped = false,
  className = '',
  getRowClassName,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (column) => {
    if (!column.sortable || !sortable) return;

    let direction = 'asc';
    if (sortConfig.key === column.key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key: column.key, direction });
    if (onSort) {
      onSort(column.key, direction);
    }
  };

  // Ordenamiento local cuando no se proporciona onSort
  const sortedData = useMemo(() => {
    if (onSort || !sortConfig.key || !data || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Manejar valores nulos/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Comparación de strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Comparación numérica y otros tipos
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, onSort]);

  const getSortIcon = (column) => {
    if (!column.sortable || !sortable) return null;

    if (sortConfig.key !== column.key) {
      return <FaIcons.FaSort className={styles.sortIcon} />;
    }

    return sortConfig.direction === 'asc' ? (
      <FaIcons.FaSortUp className={styles.sortIconActive} />
    ) : (
      <FaIcons.FaSortDown className={styles.sortIconActive} />
    );
  };

  const tableClasses = [
    styles.table,
    hoverable && styles.hoverable,
    striped && styles.striped,
    className
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={styles.container}>
        <table className={tableClasses}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={styles.th} style={column.align ? { textAlign: column.align } : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.key} className={styles.td} style={column.align ? { textAlign: column.align } : undefined}>
                    <div className="bx-skeleton" style={{ height: '20px', width: '100%' }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={styles.container}>
        <table className={tableClasses}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={styles.th} style={column.align ? { textAlign: column.align } : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <div className={styles.empty}>
          <FaIcons.FaInbox size={48} className={styles.emptyIcon} />
          <p className={styles.emptyMessage}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={tableClasses}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`${styles.th} ${column.sortable && sortable ? styles.sortable : ''}`}
                onClick={() => handleSort(column)}
                style={{ width: column.width }}
              >
                <div className={styles.thContent}>
                  <span>{column.label}</span>
                  {getSortIcon(column)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr
              key={row.id ?? row._index ?? rowIndex}
              className={[
                onRowClick ? styles.clickable : '',
                getRowClassName ? getRowClassName(row, rowIndex) : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={styles.td}
                  // `align` moves the column; `numeric` only evens the digit
                  // widths. Identifiers want the second without the first.
                  style={(column.align || column.numeric)
                    ? { textAlign: column.align, fontVariantNumeric: (column.numeric || column.align === 'right') ? 'tabular-nums' : undefined }
                    : undefined}
                >
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      // Column headers are rendered, not used as text: the incidents list
      // passes a styled <span> for the client column.
      label: PropTypes.node.isRequired,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  sortable: PropTypes.bool,
  hoverable: PropTypes.bool,
  striped: PropTypes.bool,
  className: PropTypes.string,
  getRowClassName: PropTypes.func,
};

export default Table;
